import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '@/lib/api';
import { BACKEND_URL } from '@/config';
import { setMonitoringUser } from '@/monitoring';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Seul le serveur peut effacer les cookies de session (httpOnly) : s'il ne répond pas, l'utilisateur reste connecté
export const LOGOUT_FAILED = 'Déconnexion impossible : vérifiez votre connexion et réessayez.';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = not auth, object = auth

  // Une erreur envoyée à Sentry porte l'identifiant interne, rien d'autre (T4)
  useEffect(() => { setMonitoringUser(user?.id); }, [user?.id]);

  // Jeton d'accès expiré (15 min sans « Se souvenir de moi ») : renouvellement par le cookie
  // refresh_token puis nouvel essai de la requête, une seule fois ; si le renouvellement échoue,
  // la session est terminée. Installé avant checkAuth pour couvrir aussi le premier /auth/me.
  useEffect(() => {
    let refreshing = null;
    const clients = [axios, api];
    const ids = clients.map((client) => client.interceptors.response.use(undefined, async (error) => {
      const { config, response } = error;
      if (response?.status !== 401 || !config || config._retried || /\/auth\/(login|register|refresh|logout)/.test(config.url || '')) {
        return Promise.reject(error);
      }
      config._retried = true;
      try {
        refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => { refreshing = null; });
        await refreshing;
      } catch {
        setUser(null);
        return Promise.reject(error);
      }
      return client(config);
    }));
    return () => clients.forEach((client, i) => client.interceptors.response.eject(ids[i]));
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Double authentification (T15) : le mot de passe seul n'ouvre pas la session ; la réponse porte alors
  // `mfa_required` (code à saisir) ou `mfa_setup_required` (mise en place obligatoire) et un `mfa_token`, et
  // l'écran appelle `completeLogin` avec la session renvoyée par la seconde étape.
  const login = async (email, password, rememberMe = false) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    if (!data.mfa_required && !data.mfa_setup_required) setUser(data);
    return data;
  };

  const completeLogin = (session) => setUser(session);

  const register = async (formData) => {
    const { data } = await axios.post(`${API}/auth/register`, formData);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  const updateUser = (updates) => setUser(prev => ({ ...prev, ...updates }));

  const switchRole = async (role) => {
    await axios.post(`${API}/users/me/switch-role`, { role });
    updateUser({ active_role: role });
  };

  return (
    <AuthContext.Provider value={{ user, login, completeLogin, register, logout, updateUser, switchRole, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
